import { useState, useEffect, useMemo } from 'react';
import ImageComparisonSlider from '../components/ImageComparisonSlider';
import TimelineSlider from '../components/TimelineSlider';
import './ProjectDetail.css';
import {
  ProjectFullWidth,
  ProjectSideBySide,
  ProjectSideBySideReverse,
  ProjectFooter
} from './ProjectCommon';
import projects from './projects';

// Helper function to preload images
const preloadImages = (imageUrls: string[]) => {
  imageUrls.forEach((url: string) => {
    const img = new Image();
    img.src = url;
  });
};

function HabitatProjectDetail() {
  const [year, setYear] = useState(2020);
  const years = useMemo(() => [2020, 2040, 2060, 2080, 2100], []);
  const timelineImage = `/habitat2100/timeline_${year}.webp`;

  // Get project data from projects.js
  const projectData = projects.find(p => p.id === 'habitat2100');

  // Preload all timeline images when component mounts
  useEffect(() => {
    const imageUrls = years.map(year => `/habitat2100/timeline_${year}.webp`);
    preloadImages(imageUrls);
  }, [years]);

  const footerProps = {
    title: "Habitat2100",
    description: projectData ? projectData.description : "",
    collaborators: [
      {
        name: "Sirinda Limsong",
        url: "https://www.sirindalimsong.com/"
      }
    ],
    backgroundImage: "/habitat2100/footer.webp"
  };

  return (
    <div className="project-detail-root">
      <div className="project-detail-hero">
        <img src="/habitat2100/hero_detail.webp" alt="Habitat2100 Project Hero" className="project-detail-hero-img" />
      </div>
      <div className="project-detail-content">
        <div className="project-detail-intro">
          <div className="project-detail-title">
            <h1>Habitat2100</h1>
            <p className="project-credit" style={{ fontStyle: 'italic', color: '#555', marginTop: '-10px', marginBottom: '20px' }}>
              A project by Luke Fiorante and <a href="https://www.sirindalimsong.com/" target="_blank" rel="noopener noreferrer" className="project-link">Sirinda Limsong</a>.
            </p>
          </div>
          <p>
            <b>Habitat2100</b> is an <b>interactive, web-based visualization system</b> designed to model and explore how <b>future climate scenarios</b> might alter habitat suitability for various species. We developed this tool by synthesizing climate projection data, species occurrence information, and predictive habitat modeling. The system's core visualization layers <b>predictive habitat heatmaps</b> onto an <b>interactive 3D terrain</b>, illuminating the intricate ecological dynamics that unfold as ecosystems adapt to new conditions.
          </p>
        </div>
        <div className="project-detail-process">
          <ProjectFullWidth
            image={timelineImage}
            alt="Habitat2100 interface"
          >
            <TimelineSlider
              value={years.indexOf(year)}
              onChange={setYear}
              label="Explore habitat changes from 2020 to 2100"
            />
            <h2>Motivation</h2>
            <p>
              While climate change directly threatens biodiversity by altering species habitats, observing one species' habitat loss doesn't capture the full ecological impact. A comprehensive understanding requires examining the ripple effects of such shifts across interconnected species and their relationships. This holistic approach to ecosystem vulnerability inspired Habitat2100, a tool designed to visualize habitat changes through the lens of these crucial interdependencies, starting with the American pika (<i>Ochotona princeps</i>) and its ecosystem in North America's Cascades region.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/habitat2100/habitat_model.webp" alt="Habitat2100 Predictive Model Diagram" caption="Predictive model diagram.">
            <h2>Ecological Modeling</h2>
            <p>
              Habitat2100's predictive capabilities are powered by the Maxent species distribution model, which is trained on historical climate data and species occurrence records from the last 50 years. The model forecasts habitat suitability in 20-year periods from the present day to 2100. Projected climate data is sourced from NASA's GISS-E2-1-G	CMIP6 model and spans three distinct climate scenarios representing potential trajectories: SSP1-2.6 (best case), SSP2-4.5 (base case), and SSP5-8.5 (worst case).
            </p>
          </ProjectSideBySide>

          <ImageComparisonSlider
            beforeImage="/habitat2100/worst_case.webp"
            afterImage="/habitat2100/best_case.webp"
            beforeLabel="Worst case scenario (2100)"
            afterLabel="Best case scenario (2100)"
          />

          <ProjectFullWidth>
            <h2>3D Visualization</h2>
            <p>
              Habitat2100 visualizes habitat suitability by superimposing generated heatmaps onto a 3D terrain model. These heatmaps appear as translucent layers, with distinct colors representing different species and opacity indicating habitat suitability probability. The system's interface is designed for users to intuitively see how these predicted habitats align with the physical landscape. This layered 3D presentation provides a more dynamic and engaging method for exploring ecological data than traditional static 2D maps.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/habitat2100/layers.webp" alt="Habitat2100 habitat suitability layers" caption="Each layer is a heatmap of the habitat suitability probability for a given species.">
            <h2>Interactive Analysis</h2>
            <p>
              Habitat2100 provides a rich set of interactive features to facilitate multi-dimensional analysis. Users can explore habitat shifts across different time periods using a timeline slider and can switch between various climate scenarios to see potential outcomes. The interface allows toggling the visibility of different species layers and can "explode" these layers vertically for clearer individual inspection. The interface also supports a comparison mode, which enables users to view two scenarios side-by-side. These interactions empower users to ask complex questions, such as how a species' habitat might shrink under a worst-case scenario or where it might overlap with predator habitats in the future.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/habitat2100/tech_stack.webp" alt="Habitat2100 Technical Stack Diagram" caption="Core technologies powering Habitat2100.">
            <h2>Technical Stack and Implementation</h2>
            <p>
              Habitat2100 combines a robust data processing pipeline with a dynamic 3D web visualization. The pipeline ingests climate data (WorldClim, NASA) and species occurrences (GBIF), processes them with GDAL, and employs the Maxent model to calculate habitat suitability probabilities. These probabilities are transformed into translucent heatmaps for display.  The frontend visualization is built using Three.js, relying on Mapbox for satellite imagery and elevation data.
            </p>
          </ProjectSideBySideReverse>

        </div>
      </div>
      <ProjectFooter {...footerProps} />
    </div>
  );
}

export default HabitatProjectDetail;
